import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import StagePlayer from './StagePlayer';
import IVSRealtimeChat from './IVSRealtimeChat';

interface AgendaItem {
  id: string;
  event_id: string;
  title: string;
  subtitle: string;
  time_utc: string;
  status: string;
}

const MOCK_ROOMS = [
  {
    room_id: 'intersect-main',
    room_name: 'Intersect Main Room',
    chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/A02aYaQTK0b2',
    stage_playback_url: 'https://4f8770ea05fa.us-east-1.playback.live-video.net/api/video/v1/us-east-1.390409532441.channel.ySfySp8h6cGt.m3u8',
    podium_position: '(-0.524, 2.604, -41.375)'
  },
  {
    room_id: 'intersect-room-01',
    room_name: 'Breakout Room 1',
    chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C',
    stage_playback_url: '',
    podium_position: '(10, 0, 10)'
  },
  {
    room_id: 'intersect-room-02',
    room_name: 'Breakout Room 2',
    chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/WrenAc6tOGVY',
    stage_playback_url: '',
    podium_position: '(20, 0, 20)'
  }
];

const MOCK_AGENDA: AgendaItem[] = [
  {
    id: '1',
    event_id: '22222',
    title: 'Opening Ceremony',
    subtitle: 'Welcome to Intersect Virtual Hub',
    time_utc: new Date().toISOString(),
    status: 'active'
  },
  {
    id: '2',
    event_id: '22222',
    title: 'Keynote: The Future of Cardano',
    subtitle: 'Speaker: John Doe',
    time_utc: new Date(Date.now() + 3600000).toISOString(),
    status: 'upcoming'
  },
  {
    id: '3',
    event_id: '22222',
    title: 'Panel Discussion',
    subtitle: 'Governance and Community',
    time_utc: new Date(Date.now() + 7200000).toISOString(),
    status: 'upcoming'
  }
];

// Default access token for sandbox testing
const SANDBOX_ACCESS_TOKEN = '';

const UnityGameSandboxMobile: React.FC = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(MOCK_ROOMS[0]);
  const [agendaItems] = useState<AgendaItem[]>(MOCK_AGENDA);
  const [isStageMuted, setIsStageMuted] = useState(true);
  const [debugIntersectStaff, setDebugIntersectStaff] = useState(true);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const userId = 'sandbox-mobile-user';

  const handleRoomSelect = (room: typeof MOCK_ROOMS[0]) => {
    console.log('[Sandbox Mobile] Switching to room:', room.room_name);
    setCurrentRoom(room);
    setShowMenu(false);
  };

  const handleToggleStageMute = () => {
    setIsStageMuted(!isStageMuted);
  };

  // Get current "happening now" agenda item
  const happeningNowItem = agendaItems.find(item => item.status === 'active' || item.status === 'live');
  const upcomingItems = agendaItems.filter(item => item.status === 'upcoming').slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header with Room Name and Menu Button */}
      <div className="bg-[#1a1a1a] border-b border-[#333] p-4 flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-white">Intersect Virtual Hub</h1>
          <p className="text-sm text-gray-400">{currentRoom.room_name}</p>
          <p className="text-xs text-yellow-500 mt-1">SANDBOX MODE</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors text-xs"
            title="Debug Options"
          >
            🔧
          </button>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-3 hover:bg-[#2a2a2a] rounded-lg transition-colors"
          >
            {showMenu ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>

      {/* Debug Panel */}
      {showDebugPanel && (
        <div className="bg-yellow-900 border-b border-yellow-700 p-4">
          <h3 className="text-sm font-semibold mb-3">Debug Options</h3>
          <div className="space-y-2">
            <button
              onClick={() => setDebugIntersectStaff(!debugIntersectStaff)}
              className="flex items-center gap-2 text-sm"
            >
              {debugIntersectStaff ? <FaToggleOn size={20} className="text-green-400" /> : <FaToggleOff size={20} className="text-gray-400" />}
              <span>Intersect Staff Mode</span>
            </button>
          </div>
        </div>
      )}

      {/* Hamburger Menu Overlay */}
      {showMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Select Room</h2>
              <button
                onClick={() => setShowMenu(false)}
                className="p-3 hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="space-y-3">
              {MOCK_ROOMS.map((room) => (
                <button
                  key={room.room_id}
                  onClick={() => handleRoomSelect(room)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${
                    currentRoom.room_id === room.room_id
                      ? 'bg-purple-600 text-white'
                      : 'bg-[#2a2a2a] text-white hover:bg-[#333]'
                  }`}
                >
                  <div className="font-semibold">{room.room_name}</div>
                  <div className="text-sm text-gray-300 mt-1">{room.room_id}</div>
                  {room.stage_playback_url && (
                    <div className="text-xs text-green-400 mt-1">● Live Stream Available</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Stream Player */}
        {currentRoom.stage_playback_url && (
          <div className="w-full aspect-video bg-black">
            <StagePlayer
              playbackUrl={currentRoom.stage_playback_url}
              isFullscreen={false}
              isMuted={isStageMuted}
              onToggleMute={handleToggleStageMute}
              onMinimize={() => {}}
            />
          </div>
        )}

        {!currentRoom.stage_playback_url && (
          <div className="w-full aspect-video bg-[#1a1a1a] flex items-center justify-center">
            <div className="text-center p-8">
              <p className="text-gray-400 text-lg">No stream available for this room</p>
              <p className="text-gray-500 text-sm mt-2">Select a different room from the menu</p>
            </div>
          </div>
        )}

        {/* Happening Now Section with Expandable Upcoming */}
        {happeningNowItem && (
          <div className="bg-[#1a1a1a] border-b border-[#333] p-4">
            <h3 className="text-sm font-semibold text-purple-400 mb-2">HAPPENING NOW</h3>
            <div className="bg-[#2a2a2a] rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-1">
                {new Date(happeningNowItem.time_utc).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'UTC',
                  timeZoneName: 'short'
                })}
              </div>
              <h4 className="text-lg font-bold text-white mb-1">{happeningNowItem.title}</h4>
              <p className="text-sm text-gray-300">{happeningNowItem.subtitle}</p>
            </div>

            {/* Show More Button and Upcoming Items */}
            {upcomingItems.length > 0 && (
              <div className="mt-3">
                <button
                  onClick={() => setShowUpcoming(!showUpcoming)}
                  className="w-full text-center py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {showUpcoming ? '▼ Show Less' : `▶ Show ${upcomingItems.length} More`}
                </button>
                
                {showUpcoming && (
                  <div className="space-y-2 mt-2">
                    {upcomingItems.map((item) => (
                      <div key={item.id} className="bg-[#2a2a2a] rounded-lg p-3 border-l-2 border-gray-600">
                        <div className="text-xs text-gray-400 mb-1">
                          {new Date(item.time_utc).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'UTC',
                            timeZoneName: 'short'
                          })}
                        </div>
                        <h4 className="text-sm font-semibold text-white">{item.title}</h4>
                        {item.subtitle && (
                          <p className="text-xs text-gray-400 mt-1">{item.subtitle}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chat Section - Fixed for mobile */}
        {/* <div className="bg-[#0a0a0a] p-4">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">CHAT</h3>
          <div className="relative h-[500px]" style={{ position: 'relative' }}>
            <div className="mobile-chat-container">
              <IVSRealtimeChat
                key={`chat-${currentRoom.room_id}`}
                roomArn={currentRoom.chat_room_arn}
                messagingEndpoint="wss://edge.ivschat.us-west-2.amazonaws.com"
                accessToken={SANDBOX_ACCESS_TOKEN}
                roomName={currentRoom.room_name}
                roomId={currentRoom.room_id}
                eventId="22222"
                userId={userId}
                podiumPosition={currentRoom.podium_position}
                debugIsIntersectStaff={debugIntersectStaff}
              />
            </div>
          </div>
        </div> */}
      </div>

      {/* Footer */}
      <div className="bg-[#1a1a1a] border-t border-[#333] p-4 text-center">
        <p className="text-xs text-gray-500">
          Sandbox Mobile View - To join the 3D virtual environment, please switch to desktop
        </p>
      </div>
    </div>
  );
};

export default UnityGameSandboxMobile;

